// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useMemo } from 'react';
import type { Client } from '../types/client';

export interface SoloClientResult {
  /** True when the relationship has exactly one client. */
  isSolo: boolean;
  /** The single client when {@link isSolo}, else null. */
  soloClient: Client | null;
}

/**
 * Investment & Protection Proposals PRD RQ-9 / IP-5: a relationship with
 * exactly one client never needs a "Who is the quote for?" / "Client"
 * selector — the single client is assumed and the two-life "Both" option is
 * never offered. Consumers should hide their client-selector UI entirely
 * when {@link SoloClientResult.isSolo} is true and auto-select
 * {@link SoloClientResult.soloClient}; a multi-client relationship (0, 2+
 * clients) keeps the full selector unchanged.
 */
export function useSoloClient(clients: readonly Client[]): SoloClientResult {
  return useMemo(
    () => ({
      isSolo: clients.length === 1,
      soloClient: clients.length === 1 ? clients[0] : null,
    }),
    [clients],
  );
}

export default useSoloClient;
